import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import { Login, Register } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Layout from "@/components/Layout";
import Weeks from "@/pages/Weeks";
import WeekView from "@/pages/WeekView";
import Journal from "@/pages/Journal";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Testimony from "@/pages/Testimony";
import Covenant from "@/pages/Covenant";
import Stats from "@/pages/Stats";
import Verses from "@/pages/Verses";
import ReadingMode from "@/pages/ReadingMode";
import Settings from "@/pages/Settings";
import AssessmentWizard from "@/pages/AssessmentWizard";
import AssessmentStartResults from "@/pages/AssessmentStartResults";
import AssessmentCompare from "@/pages/AssessmentCompare";

function Protected() {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen parchment flex items-center justify-center text-muted-foreground">Preparing…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><Outlet /></Layout>;
}

function RedirectIfAuthed({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Synchronous callback detection — must run BEFORE routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
      <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
      <Route path="/app" element={<Protected />}>
        <Route index element={<Weeks />} />
        <Route path="weeks/:weekNumber" element={<WeekView />} />
        <Route path="weeks/:weekNumber/read" element={<ReadingMode />} />
        <Route path="journal" element={<Journal />} />
        <Route path="groups" element={<Groups />} />
        <Route path="groups/:id" element={<GroupDetail />} />
        <Route path="testimony" element={<Testimony />} />
        <Route path="verses" element={<Verses />} />
        <Route path="covenant" element={<Covenant />} />
        <Route path="settings" element={<Settings />} />
        <Route path="stats" element={<Stats />} />
        <Route path="assessment/start" element={<AssessmentWizard type="start" />} />
        <Route path="assessment/finish" element={<AssessmentWizard type="finish" />} />
        <Route path="assessment/start/results" element={<AssessmentStartResults />} />
        <Route path="assessment/compare" element={<AssessmentCompare />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
